import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import { useAuthStore } from './store/auth';
import Layout from './components/Layout';
import { ToastContainer } from './components/ToastContainer';
import Login from './pages/Login';
import PasswordResetRequest from './pages/PasswordResetRequest';
import PasswordResetVerify from './pages/PasswordResetVerify';
import Dashboard from './pages/Dashboard';
import Organizations from './pages/Organizations';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Sites from './pages/Sites';
import Users from './pages/Users';
import Admins from './pages/Admins';
import Profile from './pages/Profile';
import AISettings from './pages/AISettings';
import Tasks from './pages/Tasks';
import Industries from './pages/Industries';
import HazardTypes from './pages/HazardTypes';
import RiskAssessment from './pages/RiskAssessment';
import Inspections from './pages/Inspections';
import MlSources from './pages/MlSources';
import Reports from './pages/Reports';
import UsageManagement from './pages/UsageManagement';
import AIUsageStats from './pages/AIUsageStats';
import ReleaseNotes from './pages/ReleaseNotes';
import ReleaseNoteFeed from './pages/ReleaseNoteFeed';
import Announcements from './pages/Announcements';
import Feedback from './pages/Feedback';
import MeasuresLibrary from './pages/MeasuresLibrary';
import Regulations from './pages/Regulations';
import AccidentCases from './pages/AccidentCases';
import WorkClassifications from './pages/WorkClassifications';
import IndustryWorkCategories from './pages/IndustryWorkCategories';
import AiMatching from './pages/AiMatching';
import FeatureFlags from './pages/FeatureFlags';
import UserGroups from './pages/UserGroups';
import MessageSend from './pages/MessageSend';
import ExternalServices from './pages/ExternalServices';
import HazardCategories from './pages/HazardCategories';
import UserFeedbackPage from './pages/UserFeedback';
import Roles from './pages/Roles';
import CompanyRoles from './pages/CompanyRoles';
import AcceptInvitation from './pages/AcceptInvitation';
import Verification from './pages/verification/Verification';
import SyncHistory from './pages/SyncHistory';
import Operations from './pages/Operations';
import OpsIncidents from './pages/OpsIncidents';
import OpsAlertSettings from './pages/OpsAlertSettings';
import StoryMonitor from './pages/StoryMonitor';
import LegalObligationCatalog from './pages/LegalObligationCatalog';
import ChemicalLinks from './pages/ChemicalLinks';
import LegalObligationOverlays from './pages/LegalObligationOverlays';
import ComposerEditorPage from './pages/report-composer/EditorPage';
import ManualPage from './pages/manual/ManualPage';
import PreviewPopoutPage from './pages/report-composer/PreviewPopoutPage';
import SimulationPage from './pages/report-composer/SimulationPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 슈퍼관리자만 접근 가능
  if (!user?.isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card padding="xl" elevation="md" className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">접근 권한 없음</h1>
          <p className="text-gray-600 mb-4">슈퍼관리자만 접근할 수 있습니다.</p>
          <Button variant="secondary" onClick={() => useAuthStore.getState().logout()}>
            로그아웃
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/password-reset" element={<PasswordResetRequest />} />
        <Route path="/password-reset/verify" element={<PasswordResetVerify />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        {/* Composer 미리보기 팝아웃 창 — 인증(슈퍼관리자) 유지, Layout(사이드바) 없이 미리보기만 */}
        <Route
          path="/report-composer/preview-popout"
          element={
            <PrivateRoute>
              <PreviewPopoutPage />
            </PrivateRoute>
          }
        />
        {/* Composer 시뮬레이션 — 인증(슈퍼관리자) 유지, Layout 없는 전체 화면(작성→최종 dry-run) */}
        <Route
          path="/report-composer/simulation"
          element={
            <PrivateRoute>
              <SimulationPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="companies" element={<Companies />} />
          <Route path="companies/:id" element={<CompanyDetail />} />
          <Route path="sites" element={<Sites />} />
          <Route path="users" element={<Users />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="inspections" element={<Inspections />} />
          {/* 머신러닝 소스 관리 — 소스는 메뉴가 아니라 이 페이지의 `?tab=`으로 늘린다 */}
          <Route path="ml-sources" element={<MlSources />} />
          <Route path="reports" element={<Reports />} />
          <Route path="admins" element={<Admins />} />
          <Route path="ai-settings" element={<AISettings />} />
          <Route path="industries" element={<Industries />} />
          <Route path="hazard-types" element={<HazardTypes />} />
          <Route path="risk-assessment" element={<RiskAssessment />} />
          <Route path="usage" element={<UsageManagement />} />
          <Route path="ai-matching" element={<AiMatching />} />
          <Route path="ai-usage" element={<AIUsageStats />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="measures" element={<MeasuresLibrary />} />
          <Route path="regulations" element={<Regulations />} />
          <Route path="accident-cases" element={<AccidentCases />} />
          <Route path="work-classifications" element={<WorkClassifications />} />
          <Route path="industry-work-categories" element={<IndustryWorkCategories />} />
          {/*
            노출면 둘 — 소비(`release-note-feed`)와 관리(`release-notes`). 라우트 이름이 **서버 base와
            같다**(`admin/release-note-feed` ↔ `admin/release-notes`) — 두 축을 같은 어휘로 두면
            다음 사람이 어느 화면이 어느 라우트를 치는지 묻지 않는다. 결정 채택 ⑥.
            ⚠️ 관리 쪽 경로를 **바꾸지 않았다** — 기존 북마크·`pathQueryKeys` 항목이 그대로 유지된다.
          */}
          <Route path="release-note-feed" element={<ReleaseNoteFeed />} />
          <Route path="release-notes" element={<ReleaseNotes />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="feature-flags" element={<FeatureFlags />} />
          <Route path="user-groups" element={<UserGroups />} />
          <Route path="message-send" element={<MessageSend />} />
          <Route path="external-services" element={<ExternalServices />} />
          <Route path="hazard-categories" element={<HazardCategories />} />
          {/* 사용자 피드백 — 새 피드백 알림(메일·웹훅) 링크가 `/user-feedback/:id`로 온다(상세 모달이 바로 뜬다) */}
          <Route path="user-feedback" element={<UserFeedbackPage />} />
          <Route path="user-feedback/:id" element={<UserFeedbackPage />} />
          <Route path="roles" element={<Roles />} />
          <Route path="company-roles" element={<CompanyRoles />} />
          <Route path="verification" element={<Verification />} />
          <Route path="sync-history" element={<SyncHistory />} />
          <Route path="operations" element={<Operations />} />
          {/* 운영 사건 — 통보 메일·웹훅 링크가 `/ops-incidents/:id`로 온다(상세 모달이 바로 뜬다) */}
          <Route path="ops-incidents" element={<OpsIncidents />} />
          <Route path="ops-incidents/:id" element={<OpsIncidents />} />
          <Route path="ops-alert-settings" element={<OpsAlertSettings />} />
          <Route path="story-monitor" element={<StoryMonitor />} />
          <Route path="legal-obligation-catalog" element={<LegalObligationCatalog />} />
          <Route path="legal-obligation-overlays" element={<LegalObligationOverlays />} />
          <Route path="chemical-links" element={<ChemicalLinks />} />
          <Route path="profile" element={<Profile />} />
          {/* 결재라인 메타 전용 페이지는 두지 않는다 — 양식별 결재선은 Composer 에디터의 양식 탭
              (approvalLineMeta), 현장 override는 현장 상세 모달의 결재선 탭(SiteApprovalLineTab) */}
          <Route path="report-composer" element={<ComposerEditorPage />} />
          {/* 매뉴얼: 전체 목록 → 매뉴얼 표지 → 문서 (3단계 — 매뉴얼은 여러 개다) */}
          <Route path="manual" element={<ManualPage />} />
          <Route path="manual/:manual" element={<ManualPage />} />
          <Route path="manual/:manual/:section/:slug" element={<ManualPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
